import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@/components/ui/IconButton';

/**
 * 「⋯」選單按鈕，給 react-bootstrap 的 `<Dropdown.Toggle as={EllipsisToggle} />` 使用。
 *
 * 只轉發 aria-expanded：react-bootstrap 注入的 className='dropdown-toggle'（不管呼叫端
 * 有沒有給都會注入）若跟著進到 icon-btn，會被 bootstrap 的 .dropdown-toggle::after 畫出
 * 一個 caret 三角、把圓形按鈕內的圖示擠偏。逐項指名而非整包 rest 展開，才不會再漏東西進來。
 * 也因為 className 會被注入，樣式改由 buttonClassName／iconClassName 指定
 * （Dropdown.Toggle 會把 size 與其他 props 原樣轉發進來）。
 * id 是刻意不轉發：Dropdown 產生的自動 id 只被 menu 拿去當 aria-labelledby，
 * 沒有時退化成空字串（規範上會被忽略），可接受。
 * 註：若日後有人替 Dropdown.Menu 加上 role="menu"，react-bootstrap 會多注入
 * aria-haspopup，記得一併轉發，否則會被這個具名解構靜默丟掉
 */
const EllipsisToggle = forwardRef(({ onClick, 'aria-expanded': expanded, size, buttonClassName, iconClassName }, ref) => {
  const { t } = useTranslation();
  return (
    <IconButton ref={ref} aria-expanded={expanded} size={size} className={buttonClassName} tooltip={t('menu')} onClick={(e) => { e.preventDefault(); onClick(e); }}>
      <FontAwesomeIcon icon={faEllipsis} className={iconClassName} />
    </IconButton>
  );
});

EllipsisToggle.displayName = 'EllipsisToggle';

EllipsisToggle.propTypes = {
  onClick: PropTypes.func.isRequired,
  'aria-expanded': PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  buttonClassName: PropTypes.string,
  iconClassName: PropTypes.string,
};

export default EllipsisToggle;
